import { adultRoles, type AdultRole } from '../../domain/adult-context';
import type { CurriculumLevel } from './database.types';
import {
  requireFirstRow,
  requireRows,
  type SupabaseDataProvider,
} from './data-provider';

export type { SupabaseDataProvider };

export type LearnerSpace = {
  id: string;
  aliasLabel: string;
  status: 'active' | 'archived';
  yearLevel: CurriculumLevel | null;
  schoolName: string | null;
};

export type RoleOption = {
  role: AdultRole;
  schoolId: string | null;
  schoolName: string | null;
  // Optional so every pre-existing teacher-only fixture/mock across the
  // codebase (none of which know about home schools) keeps compiling --
  // the real resolve_adult_context_options() RPC always returns a real
  // boolean here, mapped below.
  isHomeSchool?: boolean | undefined;
};

export type ActivatedContext = RoleOption & { routeKey: string };

function mapSpace(row: Record<string, unknown>): LearnerSpace {
  if (
    typeof row.id !== 'string' ||
    typeof row.alias_label !== 'string' ||
    (row.status !== 'active' && row.status !== 'archived') ||
    (row.year_level != null && typeof row.year_level !== 'string') ||
    (row.school_name != null && typeof row.school_name !== 'string')
  )
    throw new Error('INVALID_RESPONSE');
  return {
    id: row.id,
    aliasLabel: row.alias_label,
    status: row.status,
    yearLevel: (row.year_level as CurriculumLevel | null) ?? null,
    schoolName: row.school_name ?? null,
  };
}

function requireAdultRole(value: unknown): AdultRole {
  if (typeof value !== 'string' || !adultRoles.includes(value as AdultRole))
    throw new Error('INVALID_RESPONSE');
  return value as AdultRole;
}

// 2026-09-16 gap fix: claim_initial_role's own guard rejects a second
// claim outright (by design -- one role per email, forever), but that
// same guard also fires when THIS attempt's write actually went through
// and only the read of its own result (or a retry of this same call)
// failed afterwards -- a real, if narrow, network-timing case. Detecting
// this exact error lets the caller treat it as success and carry on,
// instead of leaving the adult stuck retrying a claim that can now never
// succeed a second time.
function isRoleAlreadyClaimedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('message' in error))
    return false;
  const { message } = error;
  return (
    typeof message === 'string' && message.includes('ROLE_ALREADY_CLAIMED')
  );
}

export function createAdultDataService(provider: SupabaseDataProvider) {
  return {
    async listLearnerSpaces() {
      const result = await provider
        .from('home_learner_spaces')
        .select('id,alias_label,status,year_level,school_name,created_at')
        .order('created_at');
      return requireRows(result).map(mapSpace);
    },
    async createLearnerSpace(
      aliasLabel: string,
      yearLevel?: CurriculumLevel | null,
      schoolName?: string | null,
    ) {
      const result = await provider.rpc('create_home_learner_space', {
        p_alias_label: aliasLabel,
        p_year_level: yearLevel ?? null,
        p_school_name: schoolName ?? null,
      });
      return mapSpace(requireFirstRow(result));
    },
    async renameLearnerSpace(
      id: string,
      aliasLabel: string,
      yearLevel?: CurriculumLevel | null,
      schoolName?: string | null,
    ) {
      const result = await provider.rpc('rename_home_learner_space', {
        p_space_id: id,
        p_alias_label: aliasLabel,
        p_year_level: yearLevel ?? null,
        p_school_name: schoolName ?? null,
      });
      return mapSpace(requireFirstRow(result));
    },
    async setLearnerSpaceArchived(id: string, archived: boolean) {
      const result = await provider.rpc('set_home_learner_space_archived', {
        p_space_id: id,
        p_archived: archived,
      });
      return mapSpace(requireFirstRow(result));
    },
    async listRoleOptions(): Promise<RoleOption[]> {
      const result = await provider.rpc('resolve_adult_context_options', {});
      return requireRows(result).map((row) => {
        if (
          typeof row.role !== 'string' ||
          (row.school_id !== null && typeof row.school_id !== 'string') ||
          (row.school_name !== null && typeof row.school_name !== 'string') ||
          typeof row.is_home_school !== 'boolean'
        )
          throw new Error('INVALID_RESPONSE');
        return {
          role: requireAdultRole(row.role),
          schoolId: row.school_id,
          schoolName: row.school_name,
          isHomeSchool: row.is_home_school,
        };
      });
    },
    async needsTeacherSchoolSetup(): Promise<boolean> {
      const result = await provider.rpc('teacher_needs_school_setup', {});
      const row = requireFirstRow(result);
      if (typeof row.needs_setup !== 'boolean')
        throw new Error('INVALID_RESPONSE');
      return row.needs_setup;
    },
    // Sign-in redesign (2026-09-15): distinguishes a genuinely brand-new
    // adult (never claimed any role -- show the role-claim screen) from a
    // genuinely revoked/denied one (had a role once, now has none -- still
    // /denied). Both look identical from listRoleOptions() alone.
    async needsInitialRoleClaim(): Promise<boolean> {
      const result = await provider.rpc('needs_initial_role_claim', {});
      const row = requireFirstRow(result);
      if (typeof row.needs_claim !== 'boolean')
        throw new Error('INVALID_RESPONSE');
      return row.needs_claim;
    },
    // Sign-in redesign (2026-09-15): role is no longer chosen before an
    // account exists, so a brand-new adult lands here with zero role
    // assignments. This is the one-time "claim your first role" step --
    // claim_initial_role (202609230004) only allows it for an adult who
    // genuinely has no role yet, so it can never be used to pick up a
    // second, unrelated role later.
    async claimInitialRole(
      role: 'parent_carer' | 'teacher' | 'school_lead',
    ): Promise<AdultRole> {
      const result = await provider.rpc('claim_initial_role', {
        p_role: role,
      });
      if (isRoleAlreadyClaimedError(result.error)) return role;
      const row = requireFirstRow(result);
      return requireAdultRole(row.role);
    },
    // One-time "no of kids, year levels" step for a freshly-claimed parent
    // (2026-09-16 flow), same role TeacherOnboarding.tsx already plays for
    // a freshly-claimed teacher's school details. security definer RPC, so
    // it reads correctly for the same adult across sessions/devices.
    async needsParentOnboarding(): Promise<boolean> {
      const result = await provider.rpc('needs_parent_onboarding', {});
      const row = requireFirstRow(result);
      if (typeof row.needs_onboarding !== 'boolean')
        throw new Error('INVALID_RESPONSE');
      return row.needs_onboarding;
    },
    async completeParentOnboarding(
      childCount: number,
      yearLevels: CurriculumLevel[],
    ): Promise<void> {
      await provider.rpc('complete_parent_onboarding', {
        p_child_count: childCount,
        p_year_levels: yearLevels,
      });
    },
    async activateRoleContext(option: RoleOption): Promise<ActivatedContext> {
      const result = await provider.rpc('activate_adult_context', {
        p_role: option.role,
        p_school_id: option.schoolId,
      });
      const row = requireFirstRow(result);
      if (
        (row.selected_school_id !== null &&
          typeof row.selected_school_id !== 'string') ||
        typeof row.route_key !== 'string'
      )
        throw new Error('INVALID_RESPONSE');
      return {
        role: requireAdultRole(row.selected_role),
        schoolId: row.selected_school_id,
        schoolName: option.schoolName,
        isHomeSchool: option.isHomeSchool,
        routeKey: row.route_key,
      };
    },
  };
}

export type AdultDataService = ReturnType<typeof createAdultDataService>;
